/**
 * ★★★ WU/U2 — "VERSÃO NOVA AVISA", e nunca recarrega sozinho.
 *
 * ## O defeito que isto conserta (medido em `docs/wu-medicao.md`)
 *
 * ⚠⚠ Uma aba aberta **nunca** repede o `index.html`: a SPA roda o código antigo **até alguém
 * recarregar**, sem limite de tempo. ★ Uma aba de sexta roda o bundle de sexta na segunda. E a
 * equipe não recarrega — ela deixa o sistema aberto o dia inteiro.
 *
 * ## ★★★ O IDENTIFICADOR É O HASH DO BUNDLE, e não um carimbo à parte
 *
 * ★★ Foi decisão da medição (§4.2): um `version.json` ou um documento no banco **podem
 * divergir** do que está no ar — alguém faz o deploy e esquece de atualizar o carimbo, e aí o
 * aviso mente nos dois sentidos. ★★★ **O hash do asset não pode divergir: ele É o arquivo.**
 *
 * ⚠ E o `/index.html` já responde sem cache (U1), então reler é de graça: **641 bytes**.
 *
 * ## ⚠⚠ O QUE ESTE MÓDULO NÃO FAZ
 *
 * ★★★ **Não recarrega.** Em nenhum caminho. Quem recarrega é a pessoa, clicando — porque a
 * medição listou o que se perde num reload: **o lote inteiro da Confirmação de Agenda, com o
 * disparo em andamento**, a mensagem sendo digitada, o áudio gravado e não enviado, e o
 * conteúdo de 9 seções de Configurações. ⚠ A única exceção é a tela de LOGIN, onde não há
 * trabalho a perder — e mesmo lá quem decide é `AppShell`, não este módulo.
 */

/**
 * ★★★ O ÚNICO LUGAR DO APP QUE RECARREGA A PÁGINA.
 *
 * ⚠⚠ Recarregar apaga trabalho não salvo — a medição da WU listou o que se perde, incluindo
 * **um disparo de confirmação de agenda em andamento**. ★★ Então a recarga tem **um dono**, e há
 * guarda de fonte (`wu.recarga.guardas.test.js`) que falha se `location.reload()` aparecer em
 * qualquer outro arquivo. ★ Quem quiser recarregar chama isto e fica no grep.
 */
export const recarregarPagina = () => globalThis.location.reload();

/** ★ De quanto em quanto tempo reler o `index.html`. 5 min: a medição mostrou que o custo é nulo. */
export const INTERVALO_MS = 5 * 60 * 1000;

/**
 * O hash do bundle principal dentro de um HTML.
 *
 * ⚠ Devolve `null` quando não acha — e `null` significa **"não sei"**, nunca "mudou".
 * ★★ É a regra da WT aplicada aqui: sem os dois lados conhecidos, não se conclui nada.
 */
export function hashDoHtml(html) {
  const achado = String(html ?? '').match(/assets\/(index-[A-Za-z0-9_-]+)\.js/);
  return achado ? achado[1] : null;
}

/**
 * O hash do bundle que está RODANDO, lido da própria tag `<script>` do documento.
 *
 * ★★★ Não é um valor injetado no build: é o endereço do arquivo que o navegador **de fato
 * carregou**. ⚠ Por isso não pode mentir — e por isso não existe o caso "o carimbo diz uma
 * coisa e o código é outra".
 */
export function versaoEmExecucao(doc = globalThis.document) {
  const tags = doc?.querySelectorAll?.('script[src]');
  for (const tag of tags ?? []) {
    const hash = hashDoHtml(tag.getAttribute('src') ?? '');
    if (hash) return hash;
  }
  return null;
}

/**
 * Relê o `index.html` e diz se a versão no ar é OUTRA.
 *
 * ★ `cache: 'no-store'` no `fetch` é cinto e suspensório: o U1 já manda o cabeçalho certo, ⚠ mas
 * uma aba velha pode estar rodando antes do U1 ter subido.
 *
 * ⚠⚠ **Falha de rede é SILÊNCIO.** Não registra erro, não mostra faixa, não conta tentativa:
 * ★★★ o Wi-Fi da clínica cair não é defeito do sistema, e encher a tela de Saúde com isso
 * esconderia os erros de verdade (é a lição do `erros.js`: ruído mata o sinal).
 */
export async function verificarVersao({
  atual,
  buscar = (url, opcoes) => fetch(url, opcoes),
  caminho = '/index.html',
} = {}) {
  if (!atual) return { novaVersao: false, motivo: 'sem-versao-atual' };
  try {
    const resposta = await buscar(caminho, { cache: 'no-store' });
    if (!resposta?.ok) return { novaVersao: false, motivo: 'resposta-nao-ok' };
    const noAr = hashDoHtml(await resposta.text());
    if (!noAr) return { novaVersao: false, motivo: 'hash-nao-encontrado' };
    return { novaVersao: noAr !== atual, noAr, motivo: null };
  } catch {
    // ⚠ Silêncio deliberado. Ver o parágrafo acima.
    return { novaVersao: false, motivo: 'rede' };
  }
}

/**
 * Liga a vigilância: a cada `INTERVALO_MS` e **ao voltar o foco da aba**.
 *
 * ★★ O `visibilitychange` é o que faz a diferença na prática: a recepcionista deixa o sistema
 * numa aba de fundo e volta a ela de hora em hora. ⚠ Esperar o timer seria perder a volta.
 *
 * ★ Devolve a função de desligar. Uma vez que avisou, **para de verificar** — a faixa já está
 * na tela e reverificar não acrescenta nada.
 */
export function vigiarVersao(aoDetectar, opcoes = {}) {
  const {
    atual = versaoEmExecucao(),
    intervalo = INTERVALO_MS,
    doc = globalThis.document,
    ...resto
  } = opcoes;

  let vivo = true;
  const parar = () => {
    if (!vivo) return;
    vivo = false;
    clearInterval(timer);
    doc?.removeEventListener?.('visibilitychange', aoVoltar);
  };

  const conferir = async () => {
    if (!vivo) return;
    const { novaVersao, noAr } = await verificarVersao({ atual, ...resto });
    if (novaVersao && vivo) {
      parar();
      aoDetectar(noAr);
    }
  };

  /**
   * ⚠ DEVOLVE a promessa de propósito: sem isso o teste não tem o que aguardar e a asserção
   * corre antes da verificação terminar. ★ No navegador o retorno é ignorado — um ouvinte de
   * evento não espera por nada —, mas quem testa precisa de um ponto de espera.
   */
  const aoVoltar = () => (doc?.visibilityState === 'visible' ? conferir() : undefined);

  const timer = setInterval(conferir, intervalo);
  doc?.addEventListener?.('visibilitychange', aoVoltar);
  return parar;
}
